import { format, nextFriday, subDays } from 'date-fns'
import { USE_OPS_MOCKS } from 'pages/Operations/Shared/opsControl'
import { IStatusPill } from 'types/ui/statusPill.interface'
import { IStoreBasketLine, IStoreCustodyRow, IStoreLedgerRow } from 'types/inventoryCounter.interface'
import { INVENTORY_DEMO_ISSUES, chargeToDisplay } from './inventoryControlDemoData'


export const OUTSTANDING_CAPTION =
  'Returnables owed back to the store. Overdue custody is a coaching conversation, then an adjustment.'

export const NATURE_REGISTRY_NOTE = 'Nature comes from the item registry once backend adds it.'

export const RETURNS_LOCAL_CAPTION =
  'Returns are session-local until the backend return endpoint lands.'

export const RETURNS_REAL_CAPTION =
  'Live assignments file a Return request through the existing assignment endpoint — the list response does not carry the holder yet, so confirm the person at the counter.'

export const RETURNS_EMPTY_CAPTION = 'Pick a worker to see what they owe back.'

export const INV_DEMO_REQUEST_SERVE_LINES: { [requestId: string]: { itemId: number; quantity: number }[] } = {
  'REQ-1039': [{ itemId: 9004, quantity: 40 }],
}

export const COUNTER_KIND_META: {
  [k in IStoreLedgerRow['kind']]: { label: string; tone: IStatusPill['status'] }
} = {
  'ISSUED': { label: 'Issued', tone: 'info' },
  'ASSIGNED': { label: 'Assigned', tone: 'accent' },
  'RETURNED': { label: 'Returned', tone: 'success' },
}

export const COUNTER_NATURE_META: {
  [k in IStoreBasketLine['nature']]: { label: string; tone: IStatusPill['status'] }
} = {
  'CONSUMABLE': { label: 'Consumable', tone: 'info' },
  'RETURNABLE': { label: 'Returnable', tone: 'accent' },
}

export const CUSTODY_STATUS_META: {
  [k in IStoreCustodyRow['status']]: { tone: IStatusPill['status']; dot: boolean }
} = {
  'OK': { tone: 'success', dot: false },
  'OVERDUE': { tone: 'danger', dot: true },
  'RETURNED': { tone: 'success', dot: false },
}

export const counterLinesSummary = (lines: { quantity: number; name: string }[]): string => {
  if (!lines.length) return '—'
  if (lines.length === 1) return `${lines[0].name} ×${lines[0].quantity}`
  const units = lines.reduce((sum, line) => sum + (line.quantity || 0), 0)
  return `${lines.length} lines · ${units} units`
}

const fridayLabel = format(nextFriday(new Date()), 'EEE d MMM')
const overdueSinceLabel = format(subDays(new Date(), 3), 'd MMM')
const yesterdayLabel = format(subDays(new Date(), 1), 'd MMM')
const iso = (daysAgo: number, time: string): string =>
  `${format(subDays(new Date(), daysAgo), 'yyyy-MM-dd')}T${time}:00`

const DEMO_CUSTODY: IStoreCustodyRow[] = [
  {
    id: 'CUS-301',
    item: 'TLS-0450 Torque wrench 3/4″ ×1',
    holder: 'D. Mensah',
    dueLabel: `Due back ${fridayLabel}`,
    status: 'OK',
    pillLabel: 'OK',
  },
  {
    id: 'CUS-302',
    item: 'RAD-0077 Two-way radio ×1',
    holder: 'Night-shift supervisor',
    dueLabel: `Was due ${overdueSinceLabel}`,
    status: 'OVERDUE',
    pillLabel: 'Overdue 3 days',
  },
  {
    id: 'CUS-299',
    item: 'RAD-0077 Two-way radio ×1',
    holder: 'A. Serwaa',
    dueLabel: `Returned ${yesterdayLabel}`,
    status: 'RETURNED',
    pillLabel: 'Returned',
  },
]

export const COUNTER_DEMO_CUSTODY: IStoreCustodyRow[] = USE_OPS_MOCKS ? DEMO_CUSTODY : []

const DEMO_LEDGER_RETURNABLES: IStoreLedgerRow[] = [
  {
    id: 'ASG-302',
    kind: 'ASSIGNED',
    dateISO: iso(2, '07:20'),
    worker: 'D. Mensah',
    itemsSummary: 'Torque wrench 3/4″ ×1',
    chargeLabel: null,
    dueBackLabel: `Due back ${fridayLabel}`,
    valueGhs: null,
  },
  {
    id: 'ASG-297',
    kind: 'ASSIGNED',
    dateISO: iso(9, '18:40'),
    worker: 'Night-shift supervisor',
    itemsSummary: 'Two-way radio — pit band ×1',
    chargeLabel: null,
    dueBackLabel: `Was due ${overdueSinceLabel}`,
    valueGhs: null,
  },
  {
    id: 'RET-299',
    kind: 'RETURNED',
    dateISO: iso(1, '17:05'),
    worker: 'A. Serwaa',
    itemsSummary: 'Two-way radio — pit band ×1',
    chargeLabel: null,
    dueBackLabel: null,
    valueGhs: null,
  },
]

const demoLedgerFromIssues = (): IStoreLedgerRow[] =>
  INVENTORY_DEMO_ISSUES.map((issue) => ({
    id: `ISS-${issue.id}`,
    kind: 'ISSUED',
    dateISO: issue.date,
    worker: issue.assignedForUser
      ? `${issue.assignedForUser.firstName} ${issue.assignedForUser.lastName}`
      : null,
    itemsSummary: counterLinesSummary(
      issue.lines.map((line) => ({ quantity: line.quantity, name: line.inventoryItem?.name ?? 'Item' })),
    ),
    chargeLabel: issue.control ? chargeToDisplay(issue.control.chargeTo) : null,
    dueBackLabel: null,
    valueGhs: issue.control?.valueGhs ?? null,
  }))

export const COUNTER_DEMO_LEDGER: IStoreLedgerRow[] = USE_OPS_MOCKS
  ? [...demoLedgerFromIssues(), ...DEMO_LEDGER_RETURNABLES]
  : []
